import HttpApis from "./HttpApis";
const REFRESH_CHECK_INTERVAL = 10000;

const Auth = {
  _intervalId: null,
  _refreshPromise: null,

  signOut() {
    Auth.stopAutoRefresh();
    localStorage.removeItem("authData");
  },

  isAuth() {
    return !!localStorage.getItem("authData");
  },

  setToken(data) {
    try {
      localStorage.setItem("authData", JSON.stringify(data));
    } catch (error) {
      console.error(error);
    }
  },

  getToken() {
    try {
      const string = localStorage.getItem("authData");
      if (!string) return null;
      return JSON.parse(string);
    } catch (error) {
      console.error(error);
      return null;
    }
  },

  clearStorage() {
    // Keep the device id (same as App.js does) so logging back in doesn't
    // register this browser as a new device.
    const deviceId = localStorage.getItem("deviceId");
    localStorage.clear();
    if (deviceId) localStorage.setItem("deviceId", deviceId);
  },

  // Only the server rejecting the refresh token (401/403, tagged by
  // Request.js) should end the session. Network errors, timeouts, 5xx and
  // captive-portal pages (e.g. waking from sleep before the network is back)
  // are transient: stay logged in and retry on the next tick.
  isSessionRejected(err) {
    return err?.httpStatus === 401 || err?.httpStatus === 403;
  },

  forceSignOut() {
    Auth.clearStorage();
    Auth.stopAutoRefresh();
    window.location.href = "/home";
  },

  isTokenExpired(expireDate) {
    if (!expireDate) return true;
    const expiryMs = expireDate * 1000;
    const now = Date.now();
    return now >= expiryMs;
  },

  isTokenExpiringSoon(expireDate) {
    if (!expireDate) return true;
    const expiryMs = expireDate * 1000;
    const now = Date.now();
    return expiryMs - now <= 60 * 60 * 1000;
  },

  // NEW: call this on app launch (same as splash screen logic)
  async checkOnAppLoad() {
    const auth = Auth.getToken();
    const token = auth?.token;
    const refreshToken = auth?.refresh_token;

    // No token and no refresh token — redirect to home
    if (!token && !refreshToken) {
      window.location.href = "/home";
      return;
    }

    // Has refresh token — try to refresh on launch
    if (refreshToken) {
      await Auth.tryRefresh();
    }

    // Start auto refresh
    Auth.startAutoRefresh();
  },

  async tryRefresh() {
    const auth = Auth.getToken();
    if (!auth?.refresh_token) return;

    const isExpired = Auth.isTokenExpired(auth.expire_date);
    const isExpiringSoon = Auth.isTokenExpiringSoon(auth.expire_date);

    if (!isExpired && !isExpiringSoon) return;

    await Auth.refreshNow();
  },

  // Refresh regardless of expire_date. Request.js calls this when the server
  // 401s an access token it no longer accepts (rotated secret, revoked
  // session) long before the token's own expiry. Concurrent callers (the
  // timer, several 401s at once) share one refresh call. Resolves to the new
  // access token, or null when there isn't one.
  refreshNow() {
    if (!Auth._refreshPromise) {
      Auth._refreshPromise = Auth._doRefresh().finally(() => {
        Auth._refreshPromise = null;
      });
    }
    return Auth._refreshPromise;
  },

  async _doRefresh() {
    const refresh_token = Auth.getToken()?.refresh_token;
    if (!refresh_token) return null;

    try {
      const res = await HttpApis.refreshToken(refresh_token);

      if (res?.data?.data) {
        const newData = {
          token: res.data.data.token,
          refresh_token: res.data.data.refresh_token,
          expire_date: res.data.data.expire_date,
        };
        Auth.setToken(newData);
        return newData.token;
      }
      console.error("Token refresh returned no data — retrying next tick");
      return null;
    } catch (err) {
      console.error("Token refresh failed:", err);
      if (Auth.isSessionRejected(err)) Auth.forceSignOut();
      return null;
    }
  },

  async checkAndRefreshOnLoad() {
    await Auth.tryRefresh();
  },

  startAutoRefresh() {
    if (Auth._intervalId) {
      clearInterval(Auth._intervalId);
    }

    Auth._intervalId = setInterval(async () => {
      await Auth.tryRefresh();
    }, REFRESH_CHECK_INTERVAL);

    return Auth._intervalId;
  },

  stopAutoRefresh() {
    if (Auth._intervalId) {
      clearInterval(Auth._intervalId);
      Auth._intervalId = null;
    }
  },
};

export default Auth;